import { faker } from '@faker-js/faker';

import {
  ReaderTaskType,
  ReaderTaskScores,
  ReaderTaskVisits,
  ReaderTaskScoringType,
  ReaderTaskScoringFormCodeTypes,
} from '@dynamika-shared/types/reader';

const spineSegments = [
  't4',
  't5',
  't6',
  't7',
  't8',
  't9',
  't10',
  't11',
  't12',
  'l1',
  'l2',
  'l3',
  'l4',
];

const grades = ['Grade0', 'Grade1', 'Grade2', 'Grade3'];

export const createScoring = (useNull?: boolean) => {
  return spineSegments.reduce((acc, segmentName) => {
    return {
      ...acc,
      [segmentName]: {
        grade: faker.helpers.arrayElement(useNull ? [...grades, null] : grades),
        comment: faker.helpers.arrayElement(useNull ? [...grades, null] : grades),
      },
    };
  }, {} as ReaderTaskScoringType);
};

export const createVisitScore = (): ReaderTaskScores => {
  return {
    patientId: faker.string.uuid(),
    visitId: faker.string.uuid(),
    assigneeId: faker.string.uuid(),
    assigneeName: faker.string.alphanumeric({
      length: { min: 3, max: 12 },
    }),
    readingCode: faker.string.alphanumeric({
      length: { min: 3, max: 12 },
    }),
    readonly: true,
    scoringFormCode: faker.helpers.arrayElement([
      'GENANT_L1_SCORING_FORM',
      'GENANT_ADJUDICATION_SCORING_FORM',
      'DXA_ELIGIBILITY_SCORING_FORM',
    ]),
    status: 'CREATED',
    scoring: createScoring(),
  };
};

export const createRandomReaderTaskVisit = (
  codeType: ReaderTaskScoringFormCodeTypes
): ReaderTaskVisits => ({
  visitId: 'visit-id',
  visitName: faker.string.alphanumeric({
    length: { min: 3, max: 12 },
  }),
  visitOrder: 0,
  acquisitionDate: faker.date.past().toISOString(),
  readonly: true,
  scoringCode: codeType,

  draftScoring: createScoring(true),
  scoring: null,
  scores:
    codeType === 'GENANT_ADJUDICATION_SCORING_FORM'
      ? [createVisitScore(), createVisitScore()]
      : [],
  reports: [
    {
      id: faker.string.uuid(),
      clinicalTrialId: faker.string.uuid(),
      patientId: faker.string.uuid(),
      visitId: faker.string.uuid(),
      readonly: true,
      referenceTaskId: faker.string.uuid(),
      tag: faker.string.alphanumeric({
        length: { min: 3, max: 12 },
      }),
      tagId: faker.string.uuid(),
      reportTemplateCode: faker.string.alphanumeric({
        length: { min: 3, max: 12 },
      }),
      status: 'CREATED',
      result: {},
    },
  ],
  dicomStudy: {
    id: faker.string.uuid(),
    visitId: faker.string.uuid(),
    studyInstanceUid: faker.string.alphanumeric({
      length: { min: 3, max: 12 },
    }),
    dicomSeries: [
      {
        id: faker.string.uuid(),
        seriesInstanceUid: faker.string.alphanumeric({
          length: { min: 3, max: 12 },
        }),
        labelId: faker.string.uuid(),
        seriesType: 'UPLOADED',
        referenceId: faker.string.uuid(),
        status: 'READY',
        hidden: true,
        acquisitionDate: '2024-08-23',
      },
    ],
  },
  dxaScore: {
    id: faker.string.uuid(),
    clinicalTrialId: faker.string.uuid(),
    patientId: faker.string.uuid(),
    visitId: faker.string.uuid(),
    readonly: true,
    scores: [
      {
        id: faker.string.uuid(),
        labelId: faker.string.uuid(),
        recordId: faker.string.alphanumeric({
          length: { min: 3, max: 12 },
        }),
        fileName: faker.string.alphanumeric({
          length: { min: 3, max: 12 },
        }),
        acquisitionDate: '2024-08-23T09:30:27.573Z',
        scanType: 0,
        label: 0,
        bodyRegion: faker.string.alphanumeric({
          length: { min: 3, max: 12 },
        }),
        bodyProperty: faker.string.alphanumeric({
          length: { min: 3, max: 12 },
        }),
        densitometryScore: {
          bmd: 0,
          bmc: 0,
          area: 0,
        },
        compositionScore: {
          fatMass: 0,
          leanMass: 0,
          boneMass: 0,
        },
        tScore: 0,
        zScore: 0,
      },
    ],
    code: faker.string.alphanumeric({
      length: { min: 3, max: 12 },
    }),
  },
});

export const createRandomTaskVisits = (
  count: number,
  codeType: ReaderTaskScoringFormCodeTypes
): ReaderTaskVisits[] => {
  return faker.helpers.multiple(() => createRandomReaderTaskVisit(codeType), {
    count,
  });
};

export function createRandomReaderTask(
  visitType: 'baseline' | 'followup',
  codeType: ReaderTaskScoringFormCodeTypes
): ReaderTaskType {
  const visits = createRandomTaskVisits(
    visitType === 'baseline' ? 1 : 2,
    codeType
  );

  if (visitType === 'followup') {
    visits[0].visitId = 'sadad';
    visits[0].scoring = createScoring();
    visits[1].visitOrder = 1;
  }

  return {
    id: faker.string.uuid(),
    readingCode: 'string',
    clinicalTrialId: faker.string.uuid(),
    patientId: faker.string.uuid(),
    visitId: 'visit-id',
    assigneeId: faker.string.uuid(),
    assigneeName: 'string',
    patientCode: 'string',
    status: 'CREATED',
    dob: faker.date.past().toISOString(),
    gender: 'string',
    parentTaskId: null,
    childTasks: [],
    ethnicity: 'string',
    startReadingTime: faker.date.past().toISOString(),
    finishReadingTime: faker.date.past().toISOString(),
    timeSpent: 0,
    visits,
  };
}
